import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useHousehold } from '../context/HouseholdContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTENSITY_COLORS = {
  rest:     { bg: 'rgba(96,165,250,0.12)',  fg: '#60a5fa', label: 'REST' },
  low:      { bg: 'rgba(0,201,167,0.12)',   fg: '#00C9A7', label: 'LOW' },
  moderate: { bg: 'rgba(0,217,255,0.12)',   fg: '#00D9FF', label: 'MOD' },
  high:     { bg: 'rgba(251,191,36,0.12)',  fg: '#fbbf24', label: 'HIGH' },
};

export default function PracticePlan() {
  const { token } = useAuth();
  const { activeAthlete } = useHousehold();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!activeAthlete) return;
    setLoading(true);
    axios.get(`${API}/practice-plan/${activeAthlete.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setPlan(r.data.plan))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, [activeAthlete, token]);

  const generate = async () => {
    if (!activeAthlete) return;
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/practice-plan/${activeAthlete.id}/generate`, {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPlan(r.data.plan);
      toast.success('New plan generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate plan');
    } finally {
      setGenerating(false);
    }
  };

  if (!activeAthlete) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Logo size={48} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, #060818, #0a0e27)'
          : 'linear-gradient(180deg, #f8fafc, #eef2f7)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <header className="max-w-4xl mx-auto px-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate('/dashboard')} className="opacity-70 hover:opacity-100">
          ← Back
        </button>
        <Logo size={28} />
        <div className="flex-1" />
        <button
          onClick={generate}
          disabled={generating}
          className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white"
          style={{
            background: generating
              ? 'rgba(150,160,180,0.3)'
              : 'linear-gradient(135deg, #00A651, #00D9FF)',
          }}
        >
          {generating ? 'Generating…' : (plan ? 'Re-generate' : 'Generate plan')}
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">7-day plan</h1>
          <p className="text-sm opacity-70 mt-1">
            For {activeAthlete.display_name || activeAthlete.full_name}.
            Adapts to your recent sessions, load, and goals.
          </p>
        </div>

        {loading && <p className="opacity-60 text-sm">Loading…</p>}

        {!loading && !plan && (
          <div
            className="rounded-2xl p-6 text-center"
            style={{
              background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
              border: '1px solid rgba(0,201,167,0.2)',
            }}
          >
            <p className="opacity-80 mb-4">No plan yet. Generate one to get started.</p>
            <button
              onClick={generate}
              disabled={generating}
              className="px-4 py-2.5 rounded-lg font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
            >
              {generating ? 'Working…' : 'Generate my plan'}
            </button>
          </div>
        )}

        {plan && (
          <>
            <PlanHeader plan={plan} theme={theme} />
            <div className="grid md:grid-cols-2 gap-4">
              {plan.days?.map((d) => <DayCard key={d.day} day={d} theme={theme} />)}
            </div>
            {plan.notes && (
              <div
                className="rounded-2xl p-4 text-sm"
                style={{
                  background: 'rgba(0,201,167,0.08)',
                  border: '1px solid rgba(0,201,167,0.25)',
                }}
              >
                <div className="text-xs uppercase tracking-widest font-bold mb-2 opacity-70">
                  Coach notes
                </div>
                {plan.notes}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function PlanHeader({ plan, theme }) {
  const themes = plan.focus_themes || [];
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: 'linear-gradient(135deg, rgba(0,166,81,0.12), rgba(0,217,255,0.10))',
        border: '1px solid rgba(0,201,167,0.25)',
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-xs opacity-70 uppercase tracking-widest">Week starting</div>
          <div className="text-xl font-extrabold">{plan.week_starting}</div>
        </div>
        <div className="text-sm opacity-80">
          Load target: <strong>{plan.load_target_total} min</strong>
        </div>
      </div>
      {themes.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {themes.map((t) => (
            <span
              key={t}
              className="text-xs px-2.5 py-1 rounded-full font-semibold"
              style={{
                background: 'rgba(0,201,167,0.18)',
                color: '#00C9A7',
                border: '1px solid rgba(0,201,167,0.35)',
              }}
            >
              {t.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function DayCard({ day, theme }) {
  const tone = INTENSITY_COLORS[day.intensity] || INTENSITY_COLORS.moderate;
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-xs opacity-60 uppercase tracking-widest">Day {day.day}</div>
          <div className="text-base font-bold">{day.date}</div>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded"
          style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.fg}55` }}
        >
          {tone.label}
        </span>
      </div>

      <div className="text-sm font-semibold mb-1">{day.focus || '—'}</div>
      <div className="text-xs opacity-70 mb-3">
        {day.session_type === 'rest' ? 'Active recovery' : day.session_type}
        {day.duration_minutes ? ` · ${day.duration_minutes} min` : ''}
      </div>

      {(day.drills || []).length > 0 && (
        <ul className="space-y-1.5 text-sm">
          {day.drills.map((d, i) => (
            <li key={`${day.day || 'day'}-${i}-${d.name}`} className="flex items-start gap-2">
              <span className="opacity-50">·</span>
              <div>
                <span className="font-semibold">{d.name}</span>
                {(d.sets || d.reps) ? (
                  <span className="opacity-70"> — {d.sets ? `${d.sets}× ` : ''}{d.reps ? `${d.reps}` : ''}</span>
                ) : null}
                {d.why && <div className="text-xs opacity-60 mt-0.5">{d.why}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
